import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './helpers/auth.interceptor';
import { aliasInterceptor } from './helpers/alias.interceptor';
import {
  NgxMaskDirective,
  provideEnvironmentNgxMask,
  provideNgxMask,
} from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { UiSecretsService } from './services/ui-secrets.service';
import { GoogleMapsLoaderService } from './services/google-maps-loader.service';
import { ThemeService } from './services/theme.service';
import { UI_SECRET_KEYS } from './constants/ui-secret-keys';
import { getStoredUser } from './helpers/user-storage.util';
import { readCookie } from './helpers/cookie.util';
import { clearCachedProfileIfSessionEnded } from './helpers/session-bootstrap.util';

// Readable, non-httpOnly hint cookie the backend sets while a staff alias
// (read-only guidance) session is active — see AliasSessionService. Checked
// below so an aliased INTERNAL staffer still gets the vendor-portal secrets
// fetch + Google Maps load, even though a plain INTERNAL bootstrap skips both.
const ALIAS_HINT_COOKIE = 'connect_alias';

export const appConfig: ApplicationConfig = {
  providers: [
    // Restores scroll position on history POP navigations (e.g. browser
    // back or workspace's Location.back()) app-wide; push navigations still
    // scroll to top.
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),
    // Order matters: functional interceptors see the request in array order
    // but the response/error in reverse, so aliasInterceptor (registered
    // after authInterceptor) sees a 401/403 error FIRST on the way back up —
    // before authInterceptor's own 401 refresh-retry can act on it — so it
    // can short-circuit the alias-session-ended case ahead of a refresh
    // attempt (see alias.interceptor.ts).
    //
    // CON-202's sessionExpiredInterceptor is deliberately not registered here.
    // It signs out a session whose bearer token the server refused, but a
    // Connect 2.0 session is an httpOnly cookie and its requests carry no
    // Authorization header, so it could never fire; authInterceptor's failed
    // refresh already clears the client and loads /login. CON-158 decides
    // whether the two converge.
    provideHttpClient(withInterceptors([authInterceptor, aliasInterceptor])),
    provideEnvironmentNgxMask(),
    NgxMaskDirective,
    provideNgxMask(),
    ToastrModule,
    provideAnimations(), // Required for ngx-toastr
    importProvidersFrom(
      ToastrModule.forRoot({
        timeOut: 5000, // Duration of toast
        positionClass: 'toast-top-center', // Position of toast
        preventDuplicates: true, // Prevent duplicate toasts
      })
    ),
    {
      // Registered FIRST — and the clear runs in the FACTORY BODY, not the
      // returned initializer function. Resolving a multi APP_INITIALIZER
      // array evaluates every provider's useFactory (and, for a provider with
      // `deps`, constructs those deps) in registration order BEFORE
      // runInitializers() invokes any of the returned functions. The
      // ThemeService initializer below has `deps: [ThemeService]`, and
      // ThemeService reads the stored user in a field initializer
      // (`resolveInitialTheme()`), so that stale read happens the moment
      // ThemeService is constructed during array resolution — earlier than
      // any returned initializer function runs. Putting the clear in the
      // returned function (deferred to runInitializers()) would run it too
      // late to prevent that stale dark stamp. Putting it here, in the
      // factory body of the FIRST provider, runs it during array resolution
      // and strictly before ThemeService's dep is resolved. See
      // session-bootstrap.util.ts for the full rationale.
      provide: APP_INITIALIZER,
      useFactory: () => {
        clearCachedProfileIfSessionEnded();
        return () => Promise.resolve();
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        uiSecrets: UiSecretsService,
        mapsLoader: GoogleMapsLoaderService
      // Initializes app secrets and loads Google Maps at startup.
      // Injected services are provided via deps array below.
      // Fails fast on error to prevent app from running with missing configuration.
      ) => {
        return () => {
          // Internal users have no map-based features; skip the secrets fetch
          // and Google Maps load entirely so no network call is made on their
          // behalf at startup. EXCEPT during an alias session — the stored
          // user is still INTERNAL (the app hasn't re-bootstrapped as the
          // vendor yet), but the staffer is viewing the vendor portal and its
          // static site-maps need the Maps key just like a real PA session.
          const isAliased = readCookie(ALIAS_HINT_COOKIE) === '1';
          if (getStoredUser()?.role === 'INTERNAL' && !isAliased) {
            return Promise.resolve();
          }
          return firstValueFrom(uiSecrets.fetchSecrets())
            .then((secrets) => {
              if (secrets?.[UI_SECRET_KEYS.GOOGLE_MAPS_API_KEY]) {
                return mapsLoader.load(secrets[UI_SECRET_KEYS.GOOGLE_MAPS_API_KEY]);
              }
              return;
            })
            .catch((error) => { throw error; });
        };
      },
      deps: [UiSecretsService, GoogleMapsLoaderService],
      multi: true,
    },
    {
      // Construct ThemeService at bootstrap so the theme attribute is resolved
      // from the stored user early (the index.html inline script already
      // applies the cached light/dark choice before first paint — but only when
      // the session-hint cookie says a session is live; otherwise it pre-paints
      // light, which is what this resolve then agrees on).
      provide: APP_INITIALIZER,
      useFactory: (_theme: ThemeService) => () => Promise.resolve(),
      deps: [ThemeService],
      multi: true,
    },
  ],
};
